import React, { useState, useContext } from 'react';
import Button from 'react-bootstrap/Button';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { addToFavorites, removeFromFavorites } from '../api/userApi';
import { User } from '../models/User';
import { NotificationContext } from '../context/NotificationContext';
import { AppContext } from '../window';
import { logDataLayerEvent } from '../utils/tagManager';
import { useTranslation } from '../i18n';

interface FavoriteButtonProps {
  user: User;
  onFavoriteChange?: (userId: number, isFavorite: boolean) => void;
  size?: 'sm' | 'lg';
  variant?: 'primary' | 'secondary' | 'outline-primary' | 'outline-secondary';
  className?: string;
  showText?: boolean;
}

export const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  user,
  onFavoriteChange,
  size = 'sm',
  variant = 'secondary',
  className = '',
  showText = false
}) => {
  const [isFavorite, setIsFavorite] = useState(!!user.isFavoriteOf);
  const [isLoading, setIsLoading] = useState(false);
  const { addNotification } = useContext(NotificationContext);
  const { texts, user: currentUser } = useContext(AppContext);
  const { t } = useTranslation();

  const handleToggleFavorite = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      let response;
      if (isFavorite) {
        response = await removeFromFavorites(user.id);
      } else {
        response = await addToFavorites(user.id);
      }

      if (response.error) {
        addNotification({
          level: 'danger',
          message: response.error
        });
      } else {
        const newFavoriteState = !isFavorite;
        setIsFavorite(newFavoriteState);
        
        // Notify parent component of the change
        onFavoriteChange?.(user.id, newFavoriteState);

        const eventName = newFavoriteState ? 'favorite_added' : 'favorite_removed';
        logDataLayerEvent(eventName, {}, currentUser);

        // Show success message
        const message = newFavoriteState 
          ? t('user.added_to_favorites', { nickname: user.nickname })
          : t('user.removed_from_favorites', { nickname: user.nickname });
        
        addNotification({
          level: 'success',
          message: message
        });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      addNotification({
        level: 'danger',
        message: t('user.error_toggling_favorite')
      });
    } finally {
      setIsLoading(false);
    }
  };

  const buttonText = isFavorite 
    ? t('user.remove_from_favorites')
    : t('user.add_to_favorites');

  return (
    <Button
      variant={variant}
      size={size}
      className={`favorite-button ${className}`}
      onClick={handleToggleFavorite}
      disabled={isLoading}
      title={buttonText}
    >
      {isFavorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
      {showText && <span className="ms-1">{buttonText}</span>}
    </Button>
  );
};